import { useSelector } from "react-redux";
import { DashSel, DashType } from "@/features/dashboard";
import React, { useContext } from "react";
import Highcharts, { Options } from "highcharts";
import { groupBy, round } from "lodash";

import { MenuMap, ReverseMenuMap } from "./constant";
import { TitleCase } from "@/utils/string";
import { useRootSelector } from "@/infrastructure/hooks";
import { oc } from "ts-optchain";

export const PerformanceContext = React.createContext<{
  country: string[];
  setCountry: (key: string[]) => void;
  selectedKey: DashType.PerformanceType[];
  setSelectedKey: (key: DashType.PerformanceType[]) => void;
}>({
  country: [],
  setCountry: () => {},
  selectedKey: [],
  setSelectedKey: () => {}
});

export const usePerformanceContext = () => useContext(PerformanceContext);

export const usePerformanceOptions = (setSelectedIndustry: (e: string | null) => void) => {
  const performance = useSelector(DashSel.performance);
  const { country, selectedKey } = usePerformanceContext();
  const stockPerfMap = groupBy(useRootSelector(DashSel.stockPerf), "industry");

  return country.map(c => getOption(c, selectedKey));

  function getOption(country: string, targets: DashType.PerformanceType[]): Options {
    const sector = performance.sector.filter(e => e.country === country);
    const industry = performance.industry.filter(e => e.country === country);

    return {
      chart: {
        type: "column"
      },
      title: {
        text: `${TitleCase(country)} Performance (%)`
      },
      xAxis: {
        type: "category"
      },
      yAxis: {
        title: {
          text: ""
        }
      },
      legend: {
        enabled: true
      },
      plotOptions: {
        series: {
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            format: "{point.y:.1f}%"
          },
          cursor: "pointer",
          point: {
            events: {
              click: function() {
                this.name && setSelectedIndustry(`${this.name}->${country}`);
              }
            }
          }
        }
      },

      tooltip: {
        formatter: function() {
          let tooltip =
            `<span style="font-size:11px">${this.series.name}</span><br>` +
            `<span style="color:${this.point.color}">${this.point.name}</span>: <b>${round(
              this.point.y as number,
              2
            )}%</b><br/>`;

          if (stockPerfMap[this.point.name]) {
            const name = this.series.name.split(" - ")[1];
            const perfType = ReverseMenuMap[name ? name.replace(" %", "") : name];
            return (
              tooltip +
              "<br/><span>--------------------</span><br/>" +
              stockPerfMap[this.point.name]
                .filter(e => e.country === country)
                .sort((a, b) => oc(b)[perfType](0) - oc(a)[perfType](0))
                .map(
                  e =>
                    `<span>[${e.name}] ${e.description} : <span style="color:${
                      oc(e)[perfType](0) >= 0 ? "green" : "red"
                    }">${round(oc(e)[perfType](0), 2)}%</span></span>`
                )
                .join("<br/>")
            );
          }

          return tooltip;
        }
      },

      series: targets.map(t => ({
        name: MenuMap[t] + " %",
        data: sector.map(s => ({
          name: s.sector,
          y: s[t],
          drilldown: `${s.sector}-${t}`
        }))
      })) as Highcharts.SeriesBarOptions[],

      drilldown: {
        series: targets.flatMap(
          t =>
            Object.entries(groupBy(industry, "sector")).map(([sector, ind]) => ({
              name: `${sector} - ${MenuMap[t]} %`,
              id: `${sector}-${t}`,
              data: ind.map(i => [i.industry, i[t]])
            })) as Highcharts.SeriesBarOptions[]
        ),
        drillUpButton: {
          position: {
            y: -40,
            x: -20
          }
        }
      }
    };
  }
};
